import axiosInstance from "@/setup/axios/axiosInstance";
import { getBackendBaseUrl } from "@/setup/apiBaseUrl";
import {
  Task,
  TaskStatus,
  TaskPriority,
  User,
  Group,
  CreateTaskRequest,
  UpdateTaskRequest,
  AssignTaskRequest,
  ChangeStatusRequest,
  CreateGroupRequest,
  UpdateGroupRequest,
  TaskResponse,
  GroupResponse,
  UserResponse,
  TaskStatusResponse,
  TaskPriorityResponse,
  TaskFilters,
  GroupFilters,
  UserFilters,
  MyTasksListResponse,
  MyTasksEmployeeOption,
  TaskComment,
  TaskNotification,
  TaskNotificationCount,
} from "../models/TaskModel";

// ==================== TASK CRUD OPERATIONS ====================

// Get tasks with filters
export const getTasks = async (filters?: TaskFilters): Promise<TaskResponse> => {
  try {
    const params = new URLSearchParams();
    params.append("no_pagination", "true");

    if (filters) {
      if (filters.content_type_id) {
        params.append("content_type_id", filters.content_type_id.toString());
      }
      if (filters.object_id) {
        params.append("object_id", filters.object_id);
      }
      if (filters.status && filters.status.length > 0) {
        filters.status.forEach((status: string) =>
          params.append("status", status)
        );
      }
      if (filters.priority && filters.priority.length > 0) {
        filters.priority.forEach((priority: string) =>
          params.append("priority", priority)
        );
      }
      if (filters.assigned_by) {
        params.append("assigned_by", filters.assigned_by);
      }
      if (filters.assigned_to_user) {
        params.append("assigned_to_user", filters.assigned_to_user);
      }
      if (filters.assigned_to_group) {
        params.append("assigned_to_group", filters.assigned_to_group);
      }
      if (filters.is_active !== undefined) {
        params.append("is_active", filters.is_active.toString());
      }
      if (filters.is_completed !== undefined) {
        params.append("is_completed", filters.is_completed.toString());
      }
      if (filters.is_overdue !== undefined) {
        params.append("is_overdue", filters.is_overdue.toString());
      }
      if (filters.created_by) {
        params.append("created_by", filters.created_by);
      }
      if (filters.due_date_from) {
        params.append("due_date__gte", filters.due_date_from);
      }
      if (filters.due_date_to) {
        params.append("due_date__lte", filters.due_date_to);
      }
      if (filters.due_date) {
        params.append("due_date", filters.due_date);
      }
      if (filters.company) {
        params.append("company", filters.company);
      }
      if (filters.assigned_employees) {
        params.append("assigned_employees", filters.assigned_employees);
      }
      if (filters.status__code) {
        params.append("status__code", filters.status__code);
      }
      if (filters.start_date_from) {
        params.append("start_date__gte", filters.start_date_from);
      }
      if (filters.start_date_to) {
        params.append("start_date__lte", filters.start_date_to);
      }
      if (filters.search) {
        params.append("search", filters.search);
      }
    }

    const url = `/tasks/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    const payload = response.data;

    if (Array.isArray(payload)) {
      return {
        results: payload,
        count: payload.length,
      };
    }

    return {
      results: Array.isArray(payload?.results) ? payload.results : [],
      count: typeof payload?.count === 'number'
        ? payload.count
        : (Array.isArray(payload?.results) ? payload.results.length : 0),
      next: payload?.next,
      previous: payload?.previous,
    };
  } catch (error) {
    console.error("Error fetching tasks:", error);
    throw error;
  }
};

// Get tasks for specific order
export const getTasksForOrder = async (orderId: string, filters?: TaskFilters): Promise<TaskResponse> => {
  const orderFilters = {
    ...filters,
    content_type_id: getContentTypeId('Order'),
    object_id: orderId,
  };
  return getTasks(orderFilters);
};

// Get task by ID
export const getTask = async (taskId: string): Promise<Task> => {
  try {
    const response = await axiosInstance.get(`/tasks/${taskId}/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching task:", error);
    throw error;
  }
};

// Create task
export const createTask = async (taskData: CreateTaskRequest): Promise<Task> => {
  try {
    const response = await axiosInstance.post("/tasks/", taskData);
    return response.data;
  } catch (error) {
    console.error("Error creating task:", error);
    throw error;
  }
};

// Update task
export const updateTask = async (taskId: string, taskData: UpdateTaskRequest): Promise<Task> => {
  try {
    const response = await axiosInstance.patch(`/tasks/${taskId}/`, taskData);
    return response.data;
  } catch (error) {
    console.error("Error updating task:", error);
    throw error;
  }
};

// Delete task
export const deleteTask = async (taskId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/tasks/${taskId}/`);
  } catch (error) {
    console.error("Error deleting task:", error);
    throw error;
  }
};

// Assign task
export const assignTask = async (taskId: string, assignData: AssignTaskRequest): Promise<Task> => {
  try {
    const response = await axiosInstance.post(`/tasks/${taskId}/assign/`, assignData);
    return response.data;
  } catch (error) {
    console.error("Error assigning task:", error);
    throw error;
  }
};

// Change task status
export const changeTaskStatus = async (taskId: string, statusData: ChangeStatusRequest): Promise<Task> => {
  try {
    const response = await axiosInstance.post(`/tasks/${taskId}/change_status/`, statusData);
    return response.data;
  } catch (error) {
    console.error("Error changing task status:", error);
    throw error;
  }
};

const appendMyTasksParams = (params: URLSearchParams, filters: TaskFilters) => {
  if (filters.is_active != null) {
    params.set('is_active', String(filters.is_active));
  }
  if (filters.only_mine) {
    params.set('only_mine', 'true');
  }
  if (filters.only_my_following) {
    params.set('only_my_following', 'true');
  }

  const company = String(filters.company || filters.company_id || '').trim();
  if (company) {
    params.set('company', company);
  }

  const search = String(filters.search || '').trim();
  if (search) {
    params.set('search', search);
  }

  if (filters.is_overdue != null) {
    params.set('is_overdue', String(filters.is_overdue));
  }
  if (filters.is_completed != null) {
    params.set('is_completed', String(filters.is_completed));
  }

  if (filters.page != null) {
    params.set('page', String(filters.page));
  }
  if (filters.page_size != null) {
    params.set('page_size', String(filters.page_size));
  }
  if (filters.get_employees) {
    params.set('get_employees', 'true');
  }

  const employeeIds = filters.employee_id
    ? (Array.isArray(filters.employee_id) ? filters.employee_id : [filters.employee_id])
    : [];
  employeeIds.forEach((id) => {
    const normalized = String(id || '').trim();
    if (normalized) {
      params.append('employee_id', normalized);
    }
  });

  const statuses = filters.status
    ? (Array.isArray(filters.status) ? filters.status : [filters.status])
    : [];
  statuses.forEach((id) => {
    const normalized = String(id || '').trim();
    if (normalized) {
      params.append('status', normalized);
    }
  });
};

const normalizeMyTasksListResponse = (payload: any): MyTasksListResponse => {
  if (Array.isArray(payload)) {
    return {
      results: payload,
      count: payload.length,
      page: 1,
      page_size: payload.length,
      total_pages: 1,
      has_next: false,
      has_previous: false,
      status_counts: {},
    };
  }

  return {
    results: Array.isArray(payload?.results) ? payload.results : [],
    count: Number(payload?.count) || 0,
    page: Number(payload?.page) || 1,
    page_size: Number(payload?.page_size) || 0,
    total_pages: Number(payload?.total_pages) || 1,
    has_next: Boolean(payload?.has_next),
    has_previous: Boolean(payload?.has_previous),
    next: payload?.next ?? null,
    previous: payload?.previous ?? null,
    status_counts: payload?.status_counts && typeof payload.status_counts === 'object'
      ? payload.status_counts
      : {},
    employees: Array.isArray(payload?.employees) ? payload.employees : undefined,
  };
};

// Get my tasks
export const getMyTasks = async (filters?: TaskFilters): Promise<MyTasksListResponse> => {
  try {
    const params = new URLSearchParams();
    if (filters) {
      appendMyTasksParams(params, filters);
    }

    const url = `/tasks/my-tasks/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    return normalizeMyTasksListResponse(response.data);
  } catch (error) {
    console.error("Error fetching my tasks:", error);
    throw error;
  }
};

export const getMyTasksEmployees = async (
  filters: Omit<TaskFilters, 'get_employees' | 'page' | 'page_size' | 'status'>
): Promise<MyTasksEmployeeOption[]> => {
  const response = await getMyTasks({
    ...filters,
    get_employees: true,
  });
  return response.employees ?? [];
};

// Get overdue tasks
export const getOverdueTasks = async (filters?: TaskFilters): Promise<TaskResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach((item) => params.append(key, item.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const url = `/tasks/overdue/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching overdue tasks:", error);
    throw error;
  }
};

// Get task statistics
export const getTaskStatistics = async () => {
  try {
    const response = await axiosInstance.get("/tasks/statistics/");
    return response.data;
  } catch (error) {
    console.error("Error fetching task statistics:", error);
    throw error;
  }
};

// ==================== TASK STATUS CRUD OPERATIONS ====================

// Get task statuses
export const getTaskStatuses = async (): Promise<TaskStatusResponse> => {
  try {
    const response = await axiosInstance.get('/settings/statuses/by-module/?module=tasks');
    const payload = response.data?.tasks || response.data;

    const statuses = Array.isArray(payload?.groups)
      ? payload.groups.flatMap((group: any) => {
          const groupStatus = group?.group_status || null;
          const items = Array.isArray(group?.items) ? group.items : [];
          return items.map((item: any) => ({
            ...item,
            group_status: item?.group_status || groupStatus,
          }));
        })
      : Array.isArray(payload?.items)
        ? payload.items
        : [];

    const mapped = statuses
      .map((status: any) => ({
        id: status.id,
        name: status.name,
        color: status.color || '#6c757d',
        is_default: status.is_default,
        is_final: status.is_final,
        sort_order: status.sort_order,
        is_active: status.is_active,
      }))
      .sort((a: any, b: any) => {
        const aSort = a.sort_order ?? 0;
        const bSort = b.sort_order ?? 0;
        if (aSort !== bSort) return aSort - bSort;
        return String(a.name || '').localeCompare(String(b.name || ''), 'vi');
      });

    return {
      results: mapped,
      count: mapped.length,
    };
  } catch (error) {
    console.error("Error fetching task statuses:", error);
    throw error;
  }
};

// Create task status
export const createTaskStatus = async (statusData: Partial<TaskStatus>): Promise<TaskStatus> => {
  try {
    const response = await axiosInstance.post("/tasks/task-statuses/", statusData);
    return response.data;
  } catch (error) {
    console.error("Error creating task status:", error);
    throw error;
  }
};

// Update task status
export const updateTaskStatus = async (statusId: string, statusData: Partial<TaskStatus>): Promise<TaskStatus> => {
  try {
    const response = await axiosInstance.patch(`/tasks/task-statuses/${statusId}/`, statusData);
    return response.data;
  } catch (error) {
    console.error("Error updating task status:", error);
    throw error;
  }
};

// Delete task status
export const deleteTaskStatus = async (statusId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/tasks/task-statuses/${statusId}/`);
  } catch (error) {
    console.error("Error deleting task status:", error);
    throw error;
  }
};

// ==================== TASK PRIORITY CRUD OPERATIONS ====================

// Get task priorities
export const getTaskPriorities = async (): Promise<TaskPriorityResponse> => {
  try {
    const response = await axiosInstance.get("/tasks/task-priorities/");
    return response.data;
  } catch (error) {
    console.error("Error fetching task priorities:", error);
    throw error;
  }
};

// Create task priority
export const createTaskPriority = async (priorityData: Partial<TaskPriority>): Promise<TaskPriority> => {
  try {
    const response = await axiosInstance.post("/tasks/task-priorities/", priorityData);
    return response.data;
  } catch (error) {
    console.error("Error creating task priority:", error);
    throw error;
  }
};

// Update task priority
export const updateTaskPriority = async (priorityId: string, priorityData: Partial<TaskPriority>): Promise<TaskPriority> => {
  try {
    const response = await axiosInstance.patch(`/tasks/task-priorities/${priorityId}/`, priorityData);
    return response.data;
  } catch (error) {
    console.error("Error updating task priority:", error);
    throw error;
  }
};

// Delete task priority
export const deleteTaskPriority = async (priorityId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/tasks/task-priorities/${priorityId}/`);
  } catch (error) {
    console.error("Error deleting task priority:", error);
    throw error;
  }
};

// ==================== GROUP CRUD OPERATIONS ====================

// Get groups
export const getGroups = async (filters?: GroupFilters): Promise<GroupResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.company) {
        params.append("company", filters.company);
      }
      if (filters.is_active !== undefined) {
        params.append("is_active", filters.is_active.toString());
      }
      if (filters.search) {
        params.append("search", filters.search);
      }
    }

    const url = `/tasks/groups/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching groups:", error);
    throw error;
  }
};

// Get group by ID
export const getGroup = async (groupId: string): Promise<Group> => {
  try {
    const response = await axiosInstance.get(`/tasks/groups/${groupId}/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching group:", error);
    throw error;
  }
};

// Create group
export const createGroup = async (groupData: CreateGroupRequest): Promise<Group> => {
  try {
    const response = await axiosInstance.post("/tasks/groups/", groupData);
    return response.data;
  } catch (error) {
    console.error("Error creating group:", error);
    throw error;
  }
};

// Update group
export const updateGroup = async (groupId: string, groupData: UpdateGroupRequest): Promise<Group> => {
  try {
    const response = await axiosInstance.patch(`/tasks/groups/${groupId}/`, groupData);
    return response.data;
  } catch (error) {
    console.error("Error updating group:", error);
    throw error;
  }
};

// Delete group
export const deleteGroup = async (groupId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/tasks/groups/${groupId}/`);
  } catch (error) {
    console.error("Error deleting group:", error);
    throw error;
  }
};

// ==================== USER OPERATIONS ====================

// Get users
export const getUsers = async (filters?: UserFilters): Promise<UserResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.is_active !== undefined) {
        params.append("is_active", filters.is_active.toString());
      }
      if (filters.search) {
        params.append("search", filters.search);
      }
    }

    const url = `/accounts/users/?${params.toString()}`;
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

// Get user by ID
export const getUser = async (userId: string): Promise<User> => {
  try {
    const response = await axiosInstance.get(`/accounts/users/${userId}/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching user:", error);
    throw error;
  }
};

// ==================== TASK COMMENTS OPERATIONS ====================

// Get task comments
export const getTaskComments = async (taskId: string): Promise<TaskComment[]> => {
  try {
    const response = await axiosInstance.get("/tasks/task-comments/", {
      params: { task: taskId, is_deleted: false, ordering: "-created_at" },
    });
    const payload = response.data;
    const rows = Array.isArray(payload) ? payload : (payload?.results || []);
    return rows as TaskComment[];
  } catch (error) {
    console.error("Error fetching task comments:", error);
    throw error;
  }
};

// Create task comment
export const createTaskComment = async (commentData: { task: string; content: string }) => {
  try {
    const response = await axiosInstance.post("/tasks/task-comments/", commentData);
    return response.data;
  } catch (error) {
    console.error("Error creating task comment:", error);
    throw error;
  }
};

// Update task comment
export const updateTaskComment = async (commentId: string, commentData: { content: string }) => {
  try {
    const response = await axiosInstance.patch(`/tasks/task-comments/${commentId}/`, commentData);
    return response.data;
  } catch (error) {
    console.error("Error updating task comment:", error);
    throw error;
  }
};

// Delete task comment
export const deleteTaskComment = async (commentId: string) => {
  try {
    await axiosInstance.delete(`/tasks/task-comments/${commentId}/`);
  } catch (error) {
    console.error("Error deleting task comment:", error);
    throw error;
  }
};

// Soft delete task comment (mark is_deleted without losing data)
export const softDeleteTaskComment = async (commentId: string): Promise<TaskComment> => {
  try {
    const response = await axiosInstance.post(`/tasks/task-comments/${commentId}/soft_delete/`, {
      is_deleted: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error soft deleting task comment:", error);
    throw error;
  }
};

// ==================== TASK NOTIFICATIONS OPERATIONS ====================

// Get notifications for the current employee (recipient resolved from token)
export const getTaskNotifications = async (
  params?: { is_read?: boolean }
): Promise<TaskNotification[]> => {
  try {
    const query: Record<string, boolean> = {};
    if (params?.is_read !== undefined) {
      query.is_read = params.is_read;
    }

    const response = await axiosInstance.get("/tasks/notifications/", { params: query });
    const payload = response.data;
    const rows = Array.isArray(payload) ? payload : (payload?.results || []);
    return rows as TaskNotification[];
  } catch (error) {
    console.error("Error fetching task notifications:", error);
    throw error;
  }
};

// Get unread notification counters for the current employee
export const getTaskUnreadCount = async (): Promise<TaskNotificationCount> => {
  try {
    const response = await axiosInstance.get("/tasks/notifications/unread_count/");
    return {
      count: Number(response.data?.count) || 0,
      task_count: Number(response.data?.task_count) || 0,
      comment_count: Number(response.data?.comment_count) || 0,
    };
  } catch (error) {
    console.error("Error fetching task notification count:", error);
    throw error;
  }
};

// Mark one notification as read
export const markTaskNotificationRead = async (id: string): Promise<TaskNotification> => {
  try {
    const response = await axiosInstance.post(`/tasks/notifications/${id}/read/`);
    return response.data;
  } catch (error) {
    console.error("Error marking task notification as read:", error);
    throw error;
  }
};

// ==================== TASK ATTACHMENTS OPERATIONS ====================

// Get task attachments
export const getTaskAttachments = async (taskId: string) => {
  try {
    const response = await axiosInstance.get("/tasks/task-attachments/", {
      params: { task: taskId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching task attachments:", error);
    throw error;
  }
};

export const getTaskAttachmentList = async (taskId: string) => {
  const response = await getTaskAttachments(taskId);
  const rows = Array.isArray(response) ? response : (response?.results || []);
  return rows.filter(
    (attachment: any) =>
      String(attachment?.description || '').trim() !== TASK_INLINE_ATTACHMENT_MARKER,
  );
};

// Create task attachment
export const createTaskAttachment = async (attachmentData: FormData) => {
  try {
    // Let the browser/Axios set the multipart boundary automatically.
    const response = await axiosInstance.post("/tasks/task-attachments/", attachmentData);
    return response.data;
  } catch (error) {
    const apiError = error as {
      response?: { status?: number; data?: unknown };
      message?: string;
    };
    console.error("Error creating task attachment:", {
      status: apiError.response?.status,
      response: apiError.response?.data,
      message: apiError.message,
    });
    throw error;
  }
};

export const resolveCreatedTaskId = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const task = payload as Record<string, unknown>;
  const directCandidates = [task.id, task.pk, task.uuid, task.task_id];

  for (const value of directCandidates) {
    const normalized = String(value ?? '').trim();
    if (normalized) {
      return normalized;
    }
  }

  const nestedCandidates = [task.task, task.data, task.result];
  for (const nested of nestedCandidates) {
    const nestedId = resolveCreatedTaskId(nested);
    if (nestedId) {
      return nestedId;
    }
  }

  return undefined;
};

// Marker for attachments that are pasted inline into task description / comments.
// They must not show up in the "File đính kèm" list.
export const TASK_INLINE_ATTACHMENT_MARKER = 'inline-image';

export const uploadTaskAttachmentFile = async (
  taskId: string,
  file: File,
  options?: { inline?: boolean },
) => {
  const formData = new FormData();
  formData.append('task', taskId);
  formData.append('file', file);
  formData.append('filename', file.name);
  formData.append('file_size', String(file.size));
  if (options?.inline) {
    formData.append('description', TASK_INLINE_ATTACHMENT_MARKER);
  }
  return createTaskAttachment(formData);
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const resolveTaskAttachmentFileUrl = (attachment: any): string => {
  const rawUrl = String(
    attachment?.file_url || attachment?.file || attachment?.url || ''
  ).trim();
  if (!rawUrl) return '';

  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
    return rawUrl;
  }

  const backendBase = getBackendBaseUrl();
  return rawUrl.startsWith('/') ? `${backendBase}${rawUrl}` : `${backendBase}/${rawUrl}`;
};

export const downloadTaskAttachment = async (
  attachmentId: string,
  fileName: string,
  attachment?: any,
) => {
  try {
    const response = await axiosInstance.get(`/tasks/task-attachments/${attachmentId}/download/`, {
      responseType: 'blob',
    });
    triggerBlobDownload(new Blob([response.data]), fileName);
    return;
  } catch {
    // Fall back to direct file URL from attachment payload.
  }

  let fileUrl = resolveTaskAttachmentFileUrl(attachment);
  if (!fileUrl) {
    const detailResponse = await axiosInstance.get(`/tasks/task-attachments/${attachmentId}/`);
    fileUrl = resolveTaskAttachmentFileUrl(detailResponse.data);
  }

  if (!fileUrl) {
    throw new Error('Không tìm thấy file để tải xuống');
  }

  const response = await axiosInstance.get(fileUrl, { responseType: 'blob' });
  triggerBlobDownload(new Blob([response.data]), fileName);
};

// Delete task attachment
export const deleteTaskAttachment = async (attachmentId: string) => {
  try {
    await axiosInstance.delete(`/tasks/task-attachments/${attachmentId}/`);
  } catch (error) {
    console.error("Error deleting task attachment:", error);
    throw error;
  }
};

// ==================== HELPER FUNCTIONS ====================

// Helper function to get content type ID
export const getContentTypeId = (modelName: string): number => {
  // This should be fetched from the backend or hardcoded based on your Django setup
  const contentTypes: { [key: string]: number } = {
    'Order': 34, // Updated to correct ContentType ID
    'Customer': 15,
    'Company': 13,
    'Task': 45,
    // Add more as needed
  };
  return contentTypes[modelName] || 34; // Default to Order ID
};
